import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { settingsAPI } from '../services/api';
import { isNative } from '../services/capacitor';
import { PUBLIC_HOST } from '../utils/hosts';

// On Capacitor the app origin is capacitor://localhost, so a relative
// /uploads/branding/foo.png resolves to capacitor://localhost/... which
// 404s. Rewrite relative URLs to the public host on native so the
// logo/favicon/background actually render.
const absolutize = (url) => {
  if (!url) return url;
  if (!isNative) return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
  return `${PUBLIC_HOST}${url.startsWith('/') ? '' : '/'}${url}`;
};

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

const DEFAULT_BRANDING = {
  logo_url: '',
  logo_dark_url: '',
  favicon_url: '',
  primary_color: '#3B82F6',
  secondary_color: '#1E40AF',
  accent_color: '#10B981',
  app_name: 'SnapText',
  tagline: '',
  login_background_url: '',
  // Login-page marketing / SEO copy (per-tenant, optional).
  login_headline: '',
  login_subheadline: '',
  login_features: [],
  login_cta_text: '',
  login_cta_url: '',
  login_seo_title: '',
  login_seo_description: '',
};

// Read last-known branding for this host from localStorage so revisits render
// with the reseller's colors/logo on the very first paint instead of flashing
// the default SnapText brand while /public/branding resolves.
function cacheKey() {
  try { return `branding:${window.location.host}`; }
  catch (e) { return 'branding:_'; }
}
function loadCachedBranding() {
  try {
    const raw = localStorage.getItem(cacheKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const merged = { ...DEFAULT_BRANDING, ...parsed };
    // Self-heal older caches that stored relative /uploads URLs before the
    // Capacitor-native absolutize() rewrite shipped. Without this the first
    // paint after upgrade still uses the broken capacitor://localhost path.
    merged.logo_url = absolutize(merged.logo_url);
    merged.logo_dark_url = absolutize(merged.logo_dark_url);
    merged.favicon_url = absolutize(merged.favicon_url);
    merged.login_background_url = absolutize(merged.login_background_url);
    return merged;
  } catch (e) { return null; }
}
function saveCachedBranding(b) {
  try { localStorage.setItem(cacheKey(), JSON.stringify(b)); } catch (e) {}
}

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(() => loadCachedBranding() || DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranding();
  }, []);

  useEffect(() => {
    // Apply CSS variables for colors
    document.documentElement.style.setProperty('--color-primary', branding.primary_color);
    document.documentElement.style.setProperty('--color-secondary', branding.secondary_color);
    document.documentElement.style.setProperty('--color-accent', branding.accent_color);

    // Update favicon
    if (branding.favicon_url) {
      const favicon = document.querySelector('link[rel="icon"]') || document.createElement('link');
      favicon.rel = 'icon';
      favicon.href = branding.favicon_url;
      if (!document.querySelector('link[rel="icon"]')) {
        document.head.appendChild(favicon);
      }
    }

    // Update page title
    if (branding.app_name) {
      const baseTitle = document.title.split(' - ').pop() || 'Dashboard';
      document.title = `${branding.app_name} - ${baseTitle}`;
    }
  }, [branding]);

  const fetchBranding = async () => {
    // First try the public endpoint (resolves reseller by host). This works pre-auth
    // so the login page can already be branded.
    let resellerBranding = null;
    try {
      const pub = await api.get('/public/branding');
      resellerBranding = pub.data || null;
    } catch (e) { /* ignore — fall through to defaults */ }

    // Then try tenant branding (auth-required). If unauth, this throws and we keep
    // reseller branding as-is. When a reseller brand is present for the current host
    // it wins over the public tenant-1 defaults so the login page reflects the reseller.
    try {
      const response = await settingsAPI.getBranding();
      const next = {
        logo_url: absolutize(resellerBranding?.logo_url || response.data.logo_url || ''),
        logo_dark_url: absolutize(response.data.logo_dark_url || ''),
        favicon_url: absolutize(resellerBranding?.favicon_url || response.data.favicon_url || ''),
        primary_color: resellerBranding?.primary_color || response.data.primary_color || '#3B82F6',
        secondary_color: response.data.secondary_color || '#1E40AF',
        accent_color: response.data.accent_color || '#10B981',
        app_name: resellerBranding?.product_name || response.data.app_name || 'SnapText',
        tagline: resellerBranding?.login_tagline || response.data.tagline || '',
        login_background_url: absolutize(response.data.login_background_url || ''),
        login_headline: response.data.login_headline || '',
        login_subheadline: response.data.login_subheadline || '',
        login_features: Array.isArray(response.data.login_features) ? response.data.login_features : [],
        login_cta_text: response.data.login_cta_text || '',
        login_cta_url: response.data.login_cta_url || '',
        login_seo_title: response.data.login_seo_title || '',
        login_seo_description: response.data.login_seo_description || '',
      };
      setBranding(next);
      saveCachedBranding(next);
    } catch (error) {
      // Unauth — apply reseller branding only
      if (resellerBranding) {
        setBranding(b => {
          const next = {
            ...b,
            logo_url: absolutize(resellerBranding.logo_url) || b.logo_url,
            favicon_url: absolutize(resellerBranding.favicon_url) || b.favicon_url,
            primary_color: resellerBranding.primary_color || b.primary_color,
            app_name: resellerBranding.product_name || b.app_name,
            tagline: resellerBranding.login_tagline || b.tagline
          };
          saveCachedBranding(next);
          return next;
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshBranding = () => {
    fetchBranding();
  };

  return (
    <BrandingContext.Provider value={{ branding, loading, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
